import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Video, FileText, Globe2, ShieldCheck, Clock, BookOpen, GraduationCap } from "lucide-react";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { api, DOCTOR } from "../lib/api";
import { DOCTOR_NOTE } from "../lib/content";

const PORTRAIT = "https://images.pexels.com/photos/32160036/pexels-photo-32160036.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function Hero({ fees }) {
  return (
    <section className="bg-gradient-to-br from-slate-50 via-blue-50/40 to-teal-50/30 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 sm:py-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 reveal">
          <span className="text-xs font-semibold tracking-wider uppercase text-teal-800 bg-teal-50 px-3 py-1 rounded-full border border-teal-200">Online Oncology Consultations</span>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-serif text-slate-900 leading-[1.08]" data-testid="hero-title">
            Understand your options <em className="text-teal-700 not-italic font-serif italic">before</em> you begin treatment.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-700 leading-relaxed max-w-xl">
            Evidence-based video consultations with {DOCTOR.name}, Medical Oncologist — for newly diagnosed patients, second opinions, and follow-ups from anywhere in the world.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {DOCTOR.qualifications.map(q => (
              <span key={q} className="inline-flex items-center gap-2 text-sm text-slate-700 bg-white border border-slate-200 rounded-full px-4 py-2">
                <GraduationCap className="w-4 h-4 text-teal-700" />{q}
              </span>
            ))}
          </div>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/book"><Button size="lg" className="bg-teal-700 hover:bg-teal-800 text-white rounded-full px-8 h-12 text-base w-full sm:w-auto" data-testid="hero-book-btn">Book a Consultation <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            <a href="#note"><Button size="lg" variant="outline" className="rounded-full px-8 h-12 text-base border-slate-300 w-full sm:w-auto" data-testid="hero-note-btn">Read a note from Dr. Porwal</Button></a>
          </div>
        </div>
        <div className="lg:col-span-5 reveal" style={{ animationDelay: "0.15s" }}>
          <div className="relative">
            <img src={PORTRAIT} alt="Dr. Jasmine Porwal" className="w-full aspect-[4/5] object-cover rounded-3xl shadow-xl" />
            <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-2xl shadow-lg border border-slate-200 p-5 w-64" data-testid="hero-fee-card">
              <div className="text-xs uppercase tracking-wider text-slate-500">20-minute video consult</div>
              <div className="mt-2 flex justify-between text-sm"><span className="text-slate-700">New patient</span><span className="font-semibold">₹{fees.new_fee?.toLocaleString("en-IN")}</span></div>
              <div className="mt-1 flex justify-between text-sm"><span className="text-slate-700">Follow-up</span><span className="font-semibold">₹{fees.followup_fee?.toLocaleString("en-IN")}</span></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  { icon: Clock, title: "Pick a time", body: "Choose a 20-minute slot. Times shown in your own timezone." },
  { icon: ShieldCheck, title: "Pay securely", body: "UPI, cards, net banking or international cards via Razorpay." },
  { icon: FileText, title: "Share reports", body: "Send your reports on WhatsApp 24–72 hours before the consult." },
  { icon: Video, title: "Meet by video", body: "Join a secure video call link sent to your email." },
];

function HowItWorks() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 sm:py-28">
      <h2 className="text-2xl sm:text-3xl font-serif text-slate-900">How the consultation works</h2>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {STEPS.map((s, i) => (
          <div key={s.title} className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md transition-shadow" data-testid={`how-step-${i + 1}`}>
            <div className="flex items-center justify-between">
              <s.icon className="w-6 h-6 text-teal-700" />
              <span className="font-serif text-3xl text-slate-200">0{i + 1}</span>
            </div>
            <div className="mt-5 font-semibold text-slate-900 text-lg">{s.title}</div>
            <p className="mt-2 text-slate-600 text-sm leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Note() {
  const paras = DOCTOR_NOTE.split("\n\n");
  return (
    <section id="note" className="max-w-4xl mx-auto px-4 sm:px-8 py-8 scroll-mt-20">
      <article className="bg-cream border border-[#F1E5D1] shadow-sm rounded-2xl p-8 sm:p-14" data-testid="doctor-note">
        <span className="text-xs font-semibold tracking-wider uppercase text-amber-800">A note from Dr. Porwal</span>
        <h2 className="mt-4 text-2xl sm:text-3xl font-serif text-slate-900 leading-snug">Why I started this portal</h2>
        <div className="mt-8 space-y-5 text-base sm:text-lg text-slate-700 leading-relaxed">
          {paras.map((p, i) => <p key={i} className={i === 0 ? "first-letter:font-serif first-letter:text-5xl first-letter:float-left first-letter:mr-2 first-letter:leading-none first-letter:text-teal-800" : ""}>{p}</p>)}
        </div>
        <div className="mt-10 pt-6 border-t border-[#F1E5D1]">
          <div className="signature">Jasmine Porwal</div>
          <div className="text-sm text-slate-600 mt-1">DM (Medical Oncology), AIIMS · MD (Internal Medicine), PGIMER</div>
        </div>
      </article>
    </section>
  );
}

const WHO = [
  { icon: BookOpen, title: "Newly diagnosed", body: "Understand every treatment option for your stage and the right order to take them in." },
  { icon: ShieldCheck, title: "Second opinions", body: "Review a proposed plan against current evidence and guidelines before you commit." },
  { icon: Globe2, title: "Outside Delhi NCR & abroad", body: "Continue follow-ups without the cost and strain of travel." },
];

function WhoFor() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 sm:py-28">
      <div className="grid lg:grid-cols-3 gap-12">
        <div>
          <h2 className="text-2xl sm:text-3xl font-serif text-slate-900">Who this is for</h2>
          <p className="mt-4 text-slate-600">An enlightened patient makes better choices. This portal exists so that distance or a rushed clinic visit never stands in the way of that.</p>
        </div>
        <div className="lg:col-span-2 grid sm:grid-cols-3 gap-6">
          {WHO.map(w => (
            <div key={w.title} className="border-t-2 border-teal-700 pt-5">
              <w.icon className="w-5 h-5 text-teal-700" />
              <div className="mt-3 font-semibold text-slate-900">{w.title}</div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{w.body}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-20 rounded-3xl bg-teal-900 text-white p-10 sm:p-14 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h3 className="font-serif text-2xl sm:text-3xl">We're here to help you understand your options.</h3>
          <p className="mt-2 text-teal-100">Slots open for the next two weeks.</p>
        </div>
        <Link to="/book"><Button size="lg" className="bg-white text-teal-900 hover:bg-teal-50 rounded-full px-8 h-12" data-testid="cta-book-btn">Choose a time</Button></Link>
      </div>
    </section>
  );
}

export default function Home() {
  const [fees, setFees] = useState({ new_fee: 1800, followup_fee: 1500 });
  useEffect(() => { api.get("/settings").then(r => setFees(r.data)).catch(() => {}); }, []);
  return (
    <Layout>
      <Hero fees={fees} />
      <HowItWorks />
      <Note />
      <WhoFor />
    </Layout>
  );
}
